export const VOICE_STATES = Object.freeze({
  IDLE: 'IDLE',
  REQUEST_MICROPHONE: 'REQUEST_MICROPHONE',
  MICROPHONE_READY: 'MICROPHONE_READY',
  LISTENING: 'LISTENING',
  VOICE_DETECTED: 'VOICE_DETECTED',
  RECORDING: 'RECORDING',
  PROCESSING_AUDIO: 'PROCESSING_AUDIO',
  WAITING_FOR_STT: 'WAITING_FOR_STT',
  WAITING_FOR_LLM: 'WAITING_FOR_LLM',
  GENERATING_TTS: 'GENERATING_TTS',
  PLAYING_AUDIO: 'PLAYING_AUDIO',
  WAITING_FOR_USER: 'WAITING_FOR_USER',
  COMPLETE: 'COMPLETE',
});

const S = VOICE_STATES;
export const LEGAL_TRANSITIONS = Object.freeze({
  [S.IDLE]: [S.REQUEST_MICROPHONE, S.WAITING_FOR_LLM, S.GENERATING_TTS, S.WAITING_FOR_USER, S.COMPLETE],
  [S.REQUEST_MICROPHONE]: [S.MICROPHONE_READY, S.IDLE, S.COMPLETE],
  [S.MICROPHONE_READY]: [S.LISTENING, S.WAITING_FOR_USER, S.IDLE, S.COMPLETE],
  [S.LISTENING]: [S.VOICE_DETECTED, S.RECORDING, S.WAITING_FOR_USER, S.IDLE, S.COMPLETE],
  [S.VOICE_DETECTED]: [S.RECORDING, S.PROCESSING_AUDIO, S.IDLE, S.COMPLETE],
  [S.RECORDING]: [S.VOICE_DETECTED, S.PROCESSING_AUDIO, S.WAITING_FOR_USER, S.IDLE, S.COMPLETE],
  [S.PROCESSING_AUDIO]: [S.WAITING_FOR_STT, S.WAITING_FOR_USER, S.IDLE, S.COMPLETE],
  [S.WAITING_FOR_STT]: [S.WAITING_FOR_LLM, S.WAITING_FOR_USER, S.IDLE, S.COMPLETE],
  [S.WAITING_FOR_LLM]: [S.GENERATING_TTS, S.WAITING_FOR_USER, S.IDLE, S.COMPLETE],
  [S.GENERATING_TTS]: [S.PLAYING_AUDIO, S.WAITING_FOR_USER, S.IDLE, S.COMPLETE],
  [S.PLAYING_AUDIO]: [S.WAITING_FOR_USER, S.IDLE, S.COMPLETE],
  [S.WAITING_FOR_USER]: [S.REQUEST_MICROPHONE, S.LISTENING, S.WAITING_FOR_LLM, S.GENERATING_TTS, S.IDLE, S.COMPLETE],
  [S.COMPLETE]: [S.IDLE],
});

export const canTransition = (from, to) => from === to || Boolean(LEGAL_TRANSITIONS[from]?.includes(to));

export const createConversationMachine = ({ conversationId, onTransition, onIllegalTransition }) => {
  let state = S.IDLE;
  let turnId = null;
  let turnNumber = 0;
  const timeline = [];
  const startedAt = performance.now();

  const transition = (nextState, metadata = {}) => {
    if (!canTransition(state, nextState)) {
      const illegal = { conversationId, turnId, from: state, to: nextState, metadata };
      onIllegalTransition?.(illegal);
      return false;
    }
    const event = {
      conversationId,
      turnId,
      turnNumber,
      from: state,
      to: nextState,
      timestamp: new Date().toISOString(),
      elapsedMs: Math.round(performance.now() - startedAt),
      metadata,
    };
    state = nextState;
    timeline.push(event);
    if (timeline.length > 300) timeline.splice(0, timeline.length - 300);
    onTransition?.(event);
    return true;
  };

  const beginTurn = () => {
    if (![S.WAITING_FOR_USER, S.MICROPHONE_READY, S.IDLE].includes(state)) return null;
    turnNumber += 1;
    turnId = `${conversationId}-turn-${turnNumber}`;
    return turnId;
  };

  return {
    beginTurn,
    getSnapshot: () => ({ state, turnId, turnNumber, timeline: [...timeline] }),
    transition,
  };
};

export const runConversationMachineSelfTest = () => {
  const illegal = [];
  const machine = createConversationMachine({ conversationId: 'stress-test', onIllegalTransition: (event) => illegal.push(event) });
  const sequence = [
    VOICE_STATES.REQUEST_MICROPHONE, VOICE_STATES.MICROPHONE_READY, VOICE_STATES.LISTENING,
    VOICE_STATES.VOICE_DETECTED, VOICE_STATES.RECORDING, VOICE_STATES.PROCESSING_AUDIO,
    VOICE_STATES.WAITING_FOR_STT, VOICE_STATES.WAITING_FOR_LLM, VOICE_STATES.GENERATING_TTS,
    VOICE_STATES.PLAYING_AUDIO, VOICE_STATES.WAITING_FOR_USER,
  ];
  machine.beginTurn();
  const legal = sequence.every((state) => machine.transition(state));
  const rejected = machine.transition(VOICE_STATES.RECORDING) === false;
  return legal && rejected && illegal.length === 1;
};
